import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Callout, DataTable, Facts, Field, Form, Loading, Page, PageHeader, Section, Unavailable, Waiting } from '@beyond-js/ui/react';
import { Allowances, Proposal, type Allowance, type AllowanceReading, type UsageReading } from '@snapshots/domain';
import { Copies, useFrame } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import type { Refusal } from '@snapshots/transport';
import { Failure, Outage, Outages, Problems } from '@snapshots/ui';
import { Changes } from './allowance';

const ORGANIZATION = /^[a-z]+_[A-Za-z0-9]{1,64}$/;

/**
 * Cloud packages allowances, for Beyond operators: an organization's allowance in force with its terms, the
 * installation's own bounds beside them, the month's measured usage (measured, never charged; the service's own
 * storage work said as such), every allowance it was given and, under them, granting, replacing or ending one
 * (`Changes`). The organization and the month are in the address (`?organization=&period=`). The page is the
 * operator's only, as Beyond Accounts says; the service decides it again on every read and change.
 *
 * What is read belongs to the operator and the organization it was read for, and only the latest read of them
 * answers: a read that comes back after the operator or the organization changed, success or refusal, is dropped,
 * and the changes start again for each operator and organization, so no review, term or outcome crosses them.
 */
export function OperatorView() {
	const [parameters, setParameters] = useSearchParams();
	const { text, client, identity, language, relative } = usePresence();
	const { arrival } = useFrame();
	const organization = parameters.get('organization') ?? '';
	const period = parameters.get('period') ?? undefined;
	const valid = ORGANIZATION.test(organization);
	const allowances = useMemo(() => new Allowances(client), [client]);
	// The proposal is the operator's for this organization: a person signing in again finds their terms
	const account = identity?.account ?? '';
	const scope = `${account}:${organization}`;
	const proposal = useMemo(() => (valid && account ? new Proposal(organization, account, { storage: globalThis.sessionStorage }) : undefined), [valid, organization, account]);
	const [reading, setReading] = useState<{ scope: string; value: AllowanceReading; at: Date }>();
	const [usage, setUsage] = useState<{ key: string; value: UsageReading['usage']; at: Date }>();
	const [problem, setProblem] = useState<{ scope: string; refusal: Refusal }>();
	const [missed, setMissed] = useState<{ scope: string; refusal: Refusal }>();
	const [written, setWritten] = useState(organization);
	// The latest read and what it is for: an answer to any other is dropped
	const latest = useRef({ generation: 0, scope });
	latest.current.scope = scope;
	useEffect(() => {
		document.title = text('page.operator');
	}, [text]);
	useEffect(() => setWritten(organization), [organization]);

	const load = useCallback(async () => {
		// A read asked for another operator or organization than the one in view now asks nothing
		if (!valid || latest.current.scope !== scope) return;
		const generation = ++latest.current.generation;
		const current = () => latest.current.generation === generation && latest.current.scope === scope;
		try {
			const [value, used] = await Promise.all([allowances.read(organization), allowances.usage(organization, period)]);
			if (!current()) return;
			setReading({ scope, value, at: new Date() });
			setUsage({ key: `${scope}:${period ?? ''}`, value: used.usage, at: new Date() });
			setProblem(undefined);
			setMissed(undefined);
		} catch (cause) {
			const refusal = Problems.refusal(cause);
			if (refusal.quiet || !current()) return;
			if (Outage.cause(refusal)) setMissed({ scope, refusal });
			else setProblem({ scope, refusal });
		}
	}, [allowances, organization, period, valid, scope]);
	useEffect(() => {
		setProblem(undefined);
		setMissed(undefined);
		void load();
	}, [load]);

	const shown = reading?.scope === scope ? reading.value : undefined;
	const refused = problem?.scope === scope ? problem.refusal : undefined;
	const unanswered = missed?.scope === scope ? missed.refusal : undefined;
	const reads = unanswered ? [{ key: 'operator', title: text('loading.operator'), refusal: unanswered, run: () => load() }] : [];
	const signed = identity?.session.authenticated;
	const minutes = Math.round((shown?.recent ?? 900) / 60);
	const header = (
		<PageHeader title={text('operator.title')} headingRef={arrival.heading} lead={text('operator.lead')} facts={signed ? text('operator.signed', { time: relative(signed), minutes }) : text('operator.signed.unknown', { minutes })} />
	);
	if (identity && !identity.operator) {
		return (
			<Page template="detail" width="standard" header={header}>
				<Unavailable kind="access" title={text('operator.denied')} reason={text('operator.denied.reason')} labels={Copies.unavailable(language)} />
			</Page>
		);
	}
	const choose = (
		<Form className="flex flex-wrap items-end gap-3" data-operator="organization" onSubmit={event => (event.preventDefault(), ORGANIZATION.test(written.trim()) && setParameters({ organization: written.trim() }))}>
			<div className="min-w-[min(24rem,100%)] flex-1">
				<Field label={text('operator.organization')} hint={text('operator.organization.hint')} error={written.trim() && !ORGANIZATION.test(written.trim()) ? text('operator.organization.wrong') : null}>
					<input name="organization" required autoComplete="off" spellCheck={false} value={written} onChange={event => setWritten(event.target.value)} />
				</Field>
			</div>
			<Button type="submit" variant="secondary" label={text('operator.organization.show')} waiting={!ORGANIZATION.test(written.trim())} />
		</Form>
	);
	return (
		<Page template="detail" width="standard" header={header}>
			<Outages reads={reads} whole={!shown} text={text} language={language} />
			<div className="flex flex-col gap-6" data-operator-page={organization || undefined}>
				{choose}
				{!valid ? (
					<p className="text-ink-muted">{text('operator.organization.none')}</p>
				) : refused ? (
					<Failure refusal={refused} text={text} locale={language} attempt="read" />
				) : !shown ? (
					unanswered ? null : <Waiting label={Loading.text({ name: text('loading.operator'), language })} language={language} kind="lines" rows={3} />
				) : (
					<>
						<Standing reading={shown} />
						<Usage usage={usage?.key === `${scope}:${period ?? ''}` ? usage : undefined} period={period} onPeriod={value => setParameters({ organization, ...(value ? { period: value } : {}) })} />
						<History allowances={shown.allowances} />
						{proposal && <Changes key={scope} reading={shown} proposal={proposal} allowances={allowances} reread={load} />}
					</>
				)}
			</div>
		</Page>
	);
}

/** Where the organization stands and the allowance in force, its terms beside the installation's own bounds */
function Standing({ reading }: { reading: AllowanceReading }) {
	const { text, language, date } = usePresence();
	const size = (bytes: number) => Proposal.size(bytes, language);
	const current = reading.current;
	const state = reading.organization.state;
	return (
		<Section title={text('operator.section.current')}>
			<div className="flex flex-col gap-3" data-operator-state={state} data-operator-current={current?.id ?? 'none'}>
				{state === 'active' ? <p>{text('operator.state.active')}</p> : <Callout tone="warning" title={text(`operator.state.${state}`)} />}
				{!current ? (
					<p>{text('operator.current.none')}</p>
				) : (
					<>
						{!current.active && current.ends && <Callout tone="info" title={text('operator.current.past', { date: date(current.ends) })} />}
						<Facts
							rows={[
								{ key: 'plan', label: text('operator.fact.plan'), value: current.plan },
								{ key: 'bytes', label: text('operator.fact.bytes'), value: current.limits.bytes !== undefined ? size(current.limits.bytes) : '—' },
								{ key: 'sources', label: text('operator.fact.sources'), value: current.limits.sources ?? '—' },
								{ key: 'granted', label: text('operator.fact.granted'), value: text('operator.fact.granted.value', { date: date(current.granted), person: current.granted_by }) },
								{ key: 'ends', label: text('operator.fact.ends'), value: current.ends ? date(current.ends) : text('operator.fact.ends.none') },
								{ key: 'reason', label: text('operator.fact.reason'), value: current.reason }
							]}
						/>
					</>
				)}
				<p className="text-small text-ink-muted">{text('operator.limits.note', { bytes: size(reading.bounds.bytes), sources: reading.bounds.sources })}</p>
			</div>
		</Section>
	);
}

/** The month's measured usage: what was measured, never a charge; the service's own storage work said as such */
function Usage({ usage, period, onPeriod }: { usage?: { value: UsageReading['usage']; at: Date }; period?: string; onPeriod: (value: string) => void }) {
	const { text, language, relative } = usePresence();
	type Line = UsageReading['usage']['lines'][number];
	const name = (line: Line) => (line.kind.startsWith('internal.') ? text('operator.usage.internal', { kind: line.kind }) : line.kind);
	const columns = [
		{ key: 'kind', label: text('operator.usage.what'), primary: true, value: name },
		{ key: 'quantity', label: text('operator.usage.quantity'), align: 'end' as const, value: (line: Line) => `${line.quantity.toLocaleString(language)} ${line.unit}` }
	];
	const month = (
		<label className="flex items-center gap-2 text-small">
			{text('operator.usage.month')}
			<input type="month" value={period ?? usage?.value.period ?? ''} onChange={event => onPeriod(event.target.value)} />
		</label>
	);
	if (!usage) return <Section title={text('operator.section.usage')} actions={month} waiting={{ language, kind: 'table', rows: 2 }} />;
	const shown = usage.value.period;
	return (
		<Section title={text('operator.section.usage')} actions={month} description={text('operator.usage.lead', { period: shown, time: relative(usage.at) })}>
			<div data-operator-usage={shown}>
				<DataTable<Line> label={text('operator.section.usage')} columns={columns} rows={usage.value.lines} rowKey={line => line.kind} empty={{ title: text('operator.usage.empty', { period: shown }) }} />
			</div>
		</Section>
	);
}

/** Every allowance the organization was given, newest first: the audit of its grants and ends */
function History({ allowances }: { allowances: Allowance[] }) {
	const { text, date, language } = usePresence();
	const size = (bytes: number) => Proposal.size(bytes, language);
	return (
		<Section title={text('operator.section.history')}>
			{allowances.length ? (
				<ul className="bui-rows" data-operator-history>
					{allowances.map(entry => (
						<li key={entry.id} className="flex flex-col gap-1 py-2" data-allowance={entry.id}>
							<span>{text('operator.history.granted', { plan: entry.plan, date: date(entry.granted), person: entry.granted_by })}</span>
							<span className="text-small text-ink-muted">
								{[entry.limits.bytes !== undefined ? size(entry.limits.bytes) : null, entry.limits.sources !== undefined ? String(entry.limits.sources) : null, entry.reason].filter(Boolean).join(' · ')}
							</span>
							<span className="text-small text-ink-muted">
								{!entry.ended ? text('operator.history.open') : entry.ended_reason === 'replaced' ? text('operator.history.replaced', { date: date(entry.ended) }) : text('operator.history.ended', { date: date(entry.ended), reason: entry.ended_reason ?? '' })}
							</span>
						</li>
					))}
				</ul>
			) : (
				<p>{text('operator.history.empty')}</p>
			)}
		</Section>
	);
}
